import React from "react";
import { Briefcase, TrendingUp } from "lucide-react";
import type { SelectOption } from "@/components/ui/responsive-select";
import { cn } from "@/lib/utils";

export interface EnhancedDealOption extends SelectOption {
  company_name?: string;
  stage?: string;
  revenue?: number;
}

export const getStageColor = (stage?: string): string => {
  switch (stage) {
    case "lead":
      return "bg-slate-100 text-slate-800 dark:bg-slate-900/40 dark:text-slate-200";
    case "qualified":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
    case "proposal":
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
    case "negotiation":
      return "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200";
    case "deal_won":
      return "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200";
    case "wtc_lost_solution_rejected":
      return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200";
    default:
      return "bg-card-100 text-gray-800 dark:text-gray-200";
  }
};

/**
 * Renderer for deal options in `ResponsiveCommandSelect`.
 * Co-located with the deal select shell, mirroring `ContactOptionPreview`.
 */
export const renderDealOption = (option: SelectOption): React.ReactNode => {
  const enhancedOption = option as EnhancedDealOption;
  return (
    <div className="flex flex-col gap-1.5 w-full">
      <div className="flex items-center gap-2">
        <span className="font-medium">{enhancedOption.label}</span>
        {enhancedOption.stage && (
          <span
            className={cn(
              "text-xs px-1.5 py-0.5 rounded-full",
              getStageColor(enhancedOption.stage)
            )}
          >
            {enhancedOption.stage}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {enhancedOption.company_name && (
          <div className="flex items-center gap-1">
            <Briefcase className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{enhancedOption.company_name}</span>
          </div>
        )}
        {enhancedOption.revenue != null && enhancedOption.revenue > 0 && (
          <div className="flex items-center gap-1">
            <TrendingUp className="h-3 w-3 flex-shrink-0" />
            <span>${Number(enhancedOption.revenue).toLocaleString()}</span>
          </div>
        )}
      </div>
    </div>
  );
};
